import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_META, fmtDate } from "@/lib/constants";
import StatusBadge from "@/components/StatusBadge";
import { AlertTriangle, Clock, ListTodo } from "lucide-react";

function Section({ title, count, tone, tasks, navigate, testid }) {
  const tones = {
    rose: "border-rose-200 bg-rose-50 text-rose-700 border-rose-100",
    amber: "border-amber-200 bg-amber-50 text-amber-700 border-amber-100",
    zinc: "border-zinc-200 bg-white text-zinc-800 border-zinc-100",
  };
  const [b] = tones[tone].split(" ");
  return (
    <div className={`border ${b} mb-6`} data-testid={testid}>
      <div className={`px-5 py-3 border-b flex items-center gap-2 ${tone === "rose" ? "border-rose-200" : tone === "amber" ? "border-amber-200" : "border-zinc-200"}`}>
        {tone === "rose" ? <AlertTriangle className="h-4 w-4 text-rose-600" /> : tone === "amber" ? <Clock className="h-4 w-4 text-amber-600" /> : <ListTodo className="h-4 w-4 text-zinc-500" />}
        <h3 className={`font-heading font-bold ${tone === "rose" ? "text-rose-700" : tone === "amber" ? "text-amber-700" : "text-zinc-900"}`}>{title}</h3>
        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white/70">{count}</span>
      </div>
      {tasks.length === 0 ? (
        <p className="p-5 text-sm text-zinc-400">Nothing here.</p>
      ) : tasks.map((t) => (
        <button key={t.task_id} onClick={() => navigate(`/project/${t.project_id}`)}
          className="w-full flex items-center gap-4 px-5 py-3 border-b border-zinc-100 last:border-0 hover:bg-black/[0.03] transition-colors text-left"
          data-testid={`myweek-task-${t.task_id}`}>
          <span className="flex-1 text-sm font-medium truncate">{t.name}</span>
          <span className="text-xs text-zinc-500 truncate hidden md:block">{t.builder ? `${t.builder} · ` : ""}{t.project_name}</span>
          <span className="text-xs text-zinc-500 w-16 text-right">{fmtDate(t.end_date)}</span>
          <StatusBadge status={t.status} />
        </button>
      ))}
    </div>
  );
}

export default function MyWeek() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/my-week").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="p-10"><div className="h-8 w-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 lg:p-10 max-w-[1000px]">
      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-1">Just for you</p>
        <h1 className="font-heading text-4xl font-black tracking-tighter">My Week</h1>
        <p className="text-zinc-500 mt-2 text-sm">{user?.name?.split(" ")[0]}, here&apos;s what needs your attention.</p>
      </header>

      <Section title="Overdue" count={data.overdue_count} tone="rose" tasks={data.overdue} navigate={navigate} testid="myweek-overdue" />
      <Section title="Due Soon" count={data.due_soon_count} tone="amber" tasks={data.due_soon} navigate={navigate} testid="myweek-duesoon" />
      <Section title="Other Active Tasks" count={data.active.length} tone="zinc" tasks={data.active} navigate={navigate} testid="myweek-active" />
    </div>
  );
}
